import type { AfarNetworkMachine, MemberEnvironmentLimits } from "@afar-research/contracts";

/**
 * What the Network and Organizations pages say. Connect refuses with a code the client may branch
 * on; each becomes the sentence that tells the person what to do next.
 */
const FAILURES: Readonly<Record<string, string>> = {
  networks_unavailable: "AFAR Networks are not available on this AFAR Connect yet.",
  network_denied: "You are not a member of that network.",
  network_manage_denied: "Only an Owner can manage this network's machines.",
  machine_not_found: "That machine is no longer on the network.",
  machine_offline: "The machine is offline. Start it, or check its network connection.",
  machine_unavailable: "The machine did not answer. Make sure its Environment Service is running.",
  limits_need_organization: "Member Environment limits apply only to an Organization's machines.",
  organization_denied: "You are not a member of that Organization.",
  organization_owner_required: "Only an Owner can do that.",
  organization_has_machines: "Remove every machine from the Organization's network first.",
  last_owner: "An Organization needs at least one Owner. Make someone else an Owner first.",
  member_not_found: "That person is no longer a member.",
  invalid_email: "Enter a valid email address.",
  invalid_organization_name: "Give the Organization a name of up to 100 characters.",
  invitation_invalid: "That invitation link is not valid.",
  invitation_wrong_email:
    "This invitation was sent to another email address. Sign in with that account.",
  invitation_expired: "This invitation has expired. Ask an Owner for a new one.",
  invitation_already_used: "This invitation has already been used.",
  invitation_revoked: "This invitation was withdrawn.",
  machine_disabled: "This machine is switched off. Its owner switches it on in Settings → Network.",
  guest_cannot_host: "A sub-machine cannot host sub-machines of its own.",
  cloud_workspace_cannot_host:
    "A Cloud Workspace does not host sub-machines. Rent another Cloud Workspace instead.",
  machine_switches_by_power: "A sub-machine or Cloud Workspace switches by starting and stopping.",
  invalid_ssh_setup: "Check the host, port, user name and password.",
  ssh_setup_limited:
    "Too many machines were set up in the last hour. Try again later, or run the one-time command on the machine.",
  ssh_setup_unavailable: "Setting machines up over SSH is not available on this AFAR Connect yet.",
  ssh_host_unknown: "That host name does not resolve. Check it and try again.",
  ssh_host_not_public:
    "That address is private, so AFAR cannot reach it over SSH. Run the one-time command on the machine instead.",
  ssh_unreachable:
    "AFAR could not reach the machine over SSH. Check the host and port, and that SSH accepts connections from the internet.",
  ssh_auth_failed: "The machine did not accept that user name and password.",
  ssh_unsupported_os:
    "AFAR sets up macOS and Linux over SSH. On Windows, run the one-time command instead.",
  ssh_needs_curl: "The machine needs curl installed first.",
  ssh_sudo_refused:
    "sudo refused that password. The account must be an administrator on the machine.",
  ssh_setup_failed:
    "The setup did not start on the machine. Try again, or run the one-time command instead.",
};

export function describeNetworkFailure(cause: unknown): string {
  const code =
    typeof cause === "object" && cause !== null && "code" in cause
      ? String((cause as { code: unknown }).code)
      : undefined;
  return (code && FAILURES[code]) ?? "AFAR Connect could not complete the request. Try again.";
}

export function describeNetworkRefusal(refusal: { readonly code: string }): string {
  return describeNetworkFailure(refusal);
}

const MINUTE = 60_000;

export function machinePresence(machine: AfarNetworkMachine, now: number): string {
  if (machine.online) return "Online";
  const lastSeen = machine.lastSeen ? Date.parse(machine.lastSeen) : Number.NaN;
  if (!Number.isFinite(lastSeen)) return "Offline";
  const minutes = Math.max(0, Math.round((now - lastSeen) / MINUTE));
  if (minutes < 1) return "Offline, seen just now";
  if (minutes < 60) return `Offline, seen ${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `Offline, seen ${hours} h ago`;
  return `Offline, seen ${Math.round(hours / 24)} days ago`;
}

const OS_NAMES: Readonly<Record<string, string>> = {
  macOS: "macOS",
  darwin: "macOS",
  linux: "Linux",
  windows: "Windows",
};

export function machinePlatform(machine: AfarNetworkMachine): string {
  const os = OS_NAMES[machine.os] ?? machine.os;
  return machine.arch ? `${os} ${machine.arch}` : os;
}

const GB = 1_000_000_000;

export function formatLimits(limits: MemberEnvironmentLimits | null): string {
  if (limits === null) return "No limits set";
  return `${limits.cpuCount} CPU · ${Math.round(limits.memoryBytes / GB)} GB memory · ${Math.round(limits.diskBytes / GB)} GB disk`;
}

/** Reads the limits form; whole CPUs and whole gigabytes, every one positive. */
export function parseLimits(input: {
  readonly cpuCount: string;
  readonly memoryGb: string;
  readonly diskGb: string;
}): MemberEnvironmentLimits | string {
  const cpu = Number(input.cpuCount);
  const memory = Number(input.memoryGb);
  const disk = Number(input.diskGb);
  if (![cpu, memory, disk].every((value) => Number.isInteger(value) && value > 0))
    return "Enter whole numbers above zero for CPUs, memory, and disk.";
  if (disk < 10) return "Give each Member Environment at least 10 GB of disk.";
  return { cpuCount: cpu, memoryBytes: memory * GB, diskBytes: disk * GB };
}

/** The invitation token an accept link carries, from `/settings/organizations?invitation=…`. */
export function invitationTokenFromSearch(search: string): string | null {
  const token = new URLSearchParams(search).get("invitation");
  return token && token.startsWith("afar_invite_") ? token : null;
}
